import { useCallback, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import { useApolloClient } from '@apollo/client'

import { TeamStaleIncidentIdsDocument } from '@/graphql/generated/hooks'
import type {
  TeamStaleIncidentIdsQuery,
  TeamStaleIncidentIdsQueryVariables,
} from '@/graphql/generated/operations'

import { TEAM_STALE_IDS_PAGE_SIZE } from '../constants/constants'

interface UseTeamStaleIncidentIdsResultIProps {
  /**
   * Every stale incident id at the given cutoff. Rejects (after reporting)
   * on any failed page — a partial id list would silently resolve less than
   * "all", so there is no partial success.
   */
  fetchAllStaleIncidentIds: (cutoff: string) => Promise<string[]>
  isFetchingIds: boolean
}

/**
 * The program-wide resolve's id scan (ENG-32272/ENG-32213):
 * bulkResolveStaleIncidents requires an explicit id list for every scope,
 * and with server-side grouping the modal no longer loads every incident —
 * so "resolve all" gathers the ids here, at click time, as an id-only
 * projection fetched sequentially page by page (`first` = zero-based page
 * index, `last` = page size). Imperative client.query rather than a hook
 * subscription because the pages are a one-shot loop with no UI bound to
 * intermediate results; network-only so a stale cache never shrinks the
 * resolve set.
 */
export const useTeamStaleIncidentIds =
  (): UseTeamStaleIncidentIdsResultIProps => {
    const client = useApolloClient()
    const [isFetchingIds, setIsFetchingIds] = useState(false)

    const fetchAllStaleIncidentIds = useCallback(
      async (cutoff: string): Promise<string[]> => {
        setIsFetchingIds(true)
        try {
          const ids: string[] = []
          let page = 0
          let totalCount = Number.POSITIVE_INFINITY
          while (ids.length < totalCount) {
            const { data } = await client.query<
              TeamStaleIncidentIdsQuery,
              TeamStaleIncidentIdsQueryVariables
            >({
              fetchPolicy: 'network-only',
              query: TeamStaleIncidentIdsDocument,
              variables: {
                actionUpdatedAtLte: cutoff,
                first: page,
                last: TEAM_STALE_IDS_PAGE_SIZE,
              },
            })
            const edges = data?.outstandingIncidents?.edges ?? []
            totalCount = data?.outstandingIncidents?.totalCount ?? 0
            for (const edge of edges) {
              if (edge?.node?.id) ids.push(edge.node.id)
            }
            // An empty page before totalCount is reached means the set
            // shrank mid-scan (incidents resolved elsewhere); stop rather
            // than loop on the same page forever.
            if (edges.length === 0) break
            page += 1
          }
          return ids
        } catch (error) {
          Sentry.captureException(error, {
            tags: { action: 'fetch_program_ids', feature: 'stale_work' },
          })
          throw error
        } finally {
          setIsFetchingIds(false)
        }
      },
      [client]
    )

    return { fetchAllStaleIncidentIds, isFetchingIds }
  }
